import { Overlay, OverlayPositionBuilder, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal } from "@angular/cdk/portal";
import { ComponentRef, Directive, ElementRef, HostListener, Input, OnInit } from "@angular/core";
import { TooltipComponent } from "./tooltip.component";

@Directive({
  selector: '[appTooltip]',
  standalone: true
})
export class TooltipDirective implements OnInit {
  @Input('appTooltip') content: string;
  @Input('appTooltipDisabled') disabled: boolean = false;

  private overlayRef: OverlayRef;
  private isOverlayDisplayed: boolean;
  private timeout: ReturnType<typeof setTimeout>;
  private isTouchMoving: boolean = false;

  constructor(
    private overlayPositionBuilder: OverlayPositionBuilder,
    private elementRef: ElementRef,
    private overlay: Overlay
  ) { }

  ngOnInit() {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom'
        }
      ])
      .withDefaultOffsetY(-10);

    this.overlayRef = this.overlay.create({ positionStrategy });
  }
  
  @HostListener('document:touchstart', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.hide();
    }
  }

  @HostListener('touchstart', ['$event'])
  onToucheStart() {
    this.isTouchMoving = false;
  }

  @HostListener('touchmove', ['$event'])
  onToucheMove() {
    this.isTouchMoving = true;
  }

  @HostListener('mouseenter', ['$event'])
  @HostListener('touchend', ['$event'])
  show(event: TouchEvent | MouseEvent) {
    if (this.disabled || this.isOverlayDisplayed || this.isTouchMoving) {
      if (event instanceof TouchEvent && this.isOverlayDisplayed) {
        this.hide();
      }
      return;
    }

    this.isOverlayDisplayed = true;
    const tooltipPortal = new ComponentPortal(TooltipComponent);

    if (this.overlayRef.hasAttached()) {
      this.hide();
    }

    const tooltipRef: ComponentRef<TooltipComponent> = this.overlayRef.attach(tooltipPortal);
    tooltipRef.instance.text = this.content;

    setTimeout(() => {
      const clientX = event instanceof MouseEvent ? event.clientX : (event as TouchEvent).changedTouches[0].clientX;
      const rect = tooltipRef.location.nativeElement.getBoundingClientRect();
      const customTooltipEl = tooltipRef.location.nativeElement.firstElementChild;
      const leftValue = Math.round((clientX - rect.x) / rect.width * 100);
      customTooltipEl.style.setProperty('--custom-left',
        leftValue < 0
          ? '4px'
          : leftValue > 100
            ? 'calc(100% - 14px)'
            : leftValue + '%'
      );
      tooltipRef.instance.show();
    }, 0);

    this.timeout = setTimeout(() => {
      this.hide();
    }, 3000);
  }

  @HostListener('mouseout')
  hide() {
    this.overlayRef.detach();
    this.isOverlayDisplayed = false;
    clearTimeout(this.timeout);
  }
}